import { ChevronsUpDown, LogOut, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@ospra/design-system/components/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ospra/design-system/components/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@ospra/design-system/components/sidebar";
import { cn } from "@ospra/design-system/lib/utils";

import { useAuth } from "@/hooks/useAuth";

export function NavUser({
  user,
  showLabel = true,
}: {
  user: { name: string; email: string };
  showLabel?: boolean;
}) {
  const { signOut } = useAuth();
  const { theme, setTheme } = useTheme();

  const initials = user.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
    : user?.email?.substring?.(0, 2).toUpperCase?.();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              /* Single element — padding transitions so avatar slides naturally */
              <SidebarMenuButton
                className={cn(
                  "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
                  !showLabel && "p-1!"
                )}
                nativeButton={false}
                size="lg"
              >
                <Avatar className="size-6 shrink-0 rounded-lg">
                  <AvatarImage
                    alt={user.name || user.email}
                    className="rounded-lg"
                    src={user.name || ""}
                  />
                  <AvatarFallback className="rounded-lg text-xs">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                {/* Text area animates width+opacity — no remount, avatar stays put */}
                <div
                  className={cn(
                    "grid flex-1 overflow-hidden text-left text-sm leading-tight transition-[max-width,opacity]",
                    showLabel
                      ? "max-w-[200px] opacity-100 delay-[180ms] duration-200"
                      : "max-w-0 opacity-0 duration-100"
                  )}
                >
                  <span className="truncate font-medium">
                    {user.name || user.email}
                  </span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
                <ChevronsUpDown
                  className={cn(
                    "ml-auto size-4 shrink-0 transition-[max-width,opacity]",
                    showLabel
                      ? "max-w-[1rem] opacity-100 delay-[180ms] duration-200"
                      : "max-w-0 opacity-0 duration-100"
                  )}
                />
              </SidebarMenuButton>
            }
          />
          <DropdownMenuContent
            align="end"
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            sideOffset={4}
          >
            <DropdownMenuItem
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              <div className="relative mr-0.5 flex h-4 w-4 items-center justify-center">
                <Sun className="dark:-rotate-90 h-4 w-4 rotate-0 scale-100 transition-all dark:scale-0" />
                <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              </div>
              {theme === "dark" ? "Light Mode" : "Dark Mode"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut()}>
              <LogOut /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
